import { DollarSign, FileSpreadsheet } from "lucide-react"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import useTranslator from "@pma/hooks/use-translator"
import BoQTable from "./boq-table"
import axios from "axios"
import { FINANCIAL_SERVER_URL, GET_MAIN_BOQ_ENDPOINT } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { BoQItem } from "./main-boq-items"
import { useQuery } from "@tanstack/react-query"
import PmsLoader from "@components/common/loader"
import { cn } from "@pma/utils/helpers"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../common/button"

interface BillPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  boqId: string
  currentBill?: any
}

export function BillPreviewDialog({
  open,
  onOpenChange,
  boqId,
  currentBill,
}: BillPreviewDialogProps) {
  const { translate, locale } = useTranslator()

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [isDisable, setIsDisable] = useState(false)
  const [triggerApi, setTriggerApi] = useState(false)
  const [changeButtonColor, setChangeButtonColor] = useState(false)

  const getMainBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
      { boq_id: boqId },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQDetails,
    isLoading: isLoadingMainBoQs,
    refetch: refetchMainBoQs,
    isError,
  } = useQuery({
    queryKey: ["main-boq", boqId, open],
    queryFn: getMainBoQDetails,
  })

  // const boqItems: BoQItem[] = mainBoQDetails?.boq_items || []
const boqItems: BoQItem[] = useMemo(() => {
  const items = mainBoQDetails?.boq_items || []

  if (!currentBill?.length) return items

  // Create a map of boq_item_id -> total amount from all bills
  const billItemMap = new Map<number, number>()

  currentBill.forEach((bill: any) => {
    bill._bill_items.forEach((billItem: any) => {
      const existing = billItemMap.get(billItem.boq_item_id) || 0
      billItemMap.set(billItem.boq_item_id, existing + billItem.amount)
    })
  })

  // Merge: set amount from currentBill where matching
  return items.map((item: any) => {
    if (billItemMap.has(item.id)) {
      return { ...item, amount: billItemMap.get(item.id) }
    }
    return item
  })
}, [mainBoQDetails?.boq_items, currentBill])

  useEffect(() => {
    if (isError) {
      newParams.delete("main")
      return navigate(`?${newParams.toString()}`)
    }
  }, [isError])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-[calc(100vh-2rem)] max-h-[95vh] w-full max-w-[91vw] flex-col overflow-hidden p-4 sm:h-[calc(100vh-4rem)] sm:p-6",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader className='mb-4 flex flex-shrink-0 flex-row items-center justify-between space-y-0'>
          <div className='min-w-0 flex-1'>
            <DialogTitle className='flex items-center gap-2 text-base sm:text-lg'>
              <FileSpreadsheet className='h-4 w-4 flex-shrink-0 text-primary sm:h-5 sm:w-5' />
              <span className='truncate'>{mainBoQDetails?.boq?.name}</span>
            </DialogTitle>
            <DialogDescription
              className={cn("mt-1 text-xs sm:text-sm", isHebrew ? "text-right" : "text-left")}
            >
              {translate("Choose quantities for each item to create an invoice")}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Scrollable content area - flex-1 makes it take remaining space */}
        <div className='-mx-4 min-h-0 flex-1 overflow-y-auto px-2 sm:-mx-6 sm:px-4'>
          {isLoadingMainBoQs && (
            <div className='flex h-full items-center justify-center'>
              <PmsLoader />
            </div>
          )}
          {!isLoadingMainBoQs && (
            <BoQTable
              context='bill-create'
              data={boqItems}
              boqId={boqId}
              revalidate={refetchMainBoQs}
              setIsDisable={setIsDisable}
              triggerApi={triggerApi}
              setChangeButtonColor={setChangeButtonColor}
              setTriggerApi={setTriggerApi}
            />
          )}
        </div>
        <DialogFooter className={isHebrew ? "!space-x-reverse" : ""}>
          <DialogClose>
            <Button variant='outline'>{translate("Cancel")}</Button>
          </DialogClose>
          <Button
            size={"sm"}
            variant={changeButtonColor ? "secondary" : "default"}
            disabled={isDisable}
            onClick={() => setTriggerApi(true)}
            loading={isDisable}
            className='w-full text-xs sm:w-auto sm:text-sm'
          >
            <DollarSign className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")} />
            {translate("Create bill")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
